/**
 * Language and unit resolution — pure functions, no browser or Node API.
 *
 * Spec: docs/02-funkcionalis-spec.md → Beállítások → Nyelv és mértékegység.
 * Plan: docs/ai/terv-2026-10-06-i18n.md.
 */

export const LOCALES = ['hu', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

export type LanguagePreference = 'auto' | Locale;

export type UnitSystem = 'metric' | 'imperial';
export type UnitPreference = 'auto' | UnitSystem;

/**
 * Regions whose everyday units are imperial (miles, °F). Everyone else is
 * metric. Area is NOT affected — it is always km² (docs/03).
 */
const IMPERIAL_REGIONS: ReadonlySet<string> = new Set(['US', 'LR', 'MM']);

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/** `hu-HU` → `hu`, `en_US` → `en`. Lowercase, empty when unparsable. */
export function languageOf(tag: string): string {
  return tag.trim().split(/[-_]/)[0]?.toLowerCase() ?? '';
}

/**
 * `en-US` → `US`, `zh-Hant-TW` → `TW`, `hu` → `null`.
 * The region is the first 2-letter or 3-digit subtag after the language.
 */
export function regionOf(tag: string): string | null {
  const parts = tag.trim().split(/[-_]/).slice(1);
  const region = parts.find((part) => /^[A-Za-z]{2}$/.test(part) || /^\d{3}$/.test(part));
  return region ? region.toUpperCase() : null;
}

/**
 * Hungarian device → Hungarian, anything else → English (Geri, #81).
 * Only the device's TOP language counts: a list of `[en-US, hu-HU]` means
 * the user chose English first.
 */
export function resolveLocale(preference: LanguagePreference, deviceTags: readonly string[]): Locale {
  if (preference !== 'auto') return preference;
  return languageOf(deviceTags[0] ?? '') === 'hu' ? 'hu' : 'en';
}

/** Imperial only in the US, Liberia and Myanmar (Geri, #81), metric elsewhere. */
export function resolveUnitSystem(preference: UnitPreference, deviceTags: readonly string[]): UnitSystem {
  if (preference !== 'auto') return preference;
  const region = deviceTags.map(regionOf).find((r): r is string => r !== null);
  return region !== undefined && IMPERIAL_REGIONS.has(region) ? 'imperial' : 'metric';
}

/**
 * BCP 47 tag for NUMBERS: `hu-HU` or `en-US`, nothing else.
 *
 * ⚠️ Not the device region: CLDR gives `en-HU` and `en-DE` the local
 * separators („1 234,5", „1.234,5"), which inside an English UI reads as
 * a typo — measured on Geri's machine (`hu-HU-technl` → `en-HU`, 2026-10-06).
 */
export function numberTag(locale: Locale): string {
  return locale === 'hu' ? 'hu-HU' : 'en-US';
}

/**
 * BCP 47 tag for DATES and times. Hungarian is always `hu-HU`. English keeps
 * the device region when it has one, so a German device in English shows
 * `6 Oct 2026` and 24-hour time while a US device shows `Oct 6, 2026` —
 * falling back to `en-US`.
 */
export function dateTag(locale: Locale, deviceTags: readonly string[]): string {
  if (locale === 'hu') return 'hu-HU';
  const region = deviceTags.map(regionOf).find((r): r is string => r !== null);
  if (!region) return 'en-US';
  const tag = `en-${region}`;
  try {
    return Intl.DateTimeFormat.supportedLocalesOf([tag]).length > 0 ? tag : 'en-US';
  } catch {
    return 'en-US';
  }
}
